import { InfoIcon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import Select, { SingleValue } from 'react-select'

import { useIsRemoteLockBuildingConnected } from '@/api/property-keyless/remote-lock/use-is-remote-lock-building-connected'
import { Alert } from '@/components/ui/alert'
import { Tooltip } from '@/components/ui/tooltip'

import API from '../../../../api/api'
import Loader from '../../../../components/Loader/Loader'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { defaultDropdownStyles } from '../helpers'
import RemoteLockIssueFobModal from './RemoteLockIssueFobModal'

const FOB_TOOLTIP_TEXT =
  'The physical Fob/Card could be issued only if the resident already has assigned credentials within the Remote Lock system. If still not, they can be specified in a block above where you create a new Remote Lock user, connect to an existing Remote Lock user, or doors, zones, access levels and expiration date could be assigned.'

interface LockOption {
  id: string
  name: string
  label: string
  text: string
}

interface RemoteLockSectionProps {
  isAvailable: boolean
  isFobAvailable?: boolean
  isIntegratedId?: string | null
  buildingId: number
  userId: number
  clientId: number
  showExpiry?: boolean
}

const formatFobDate = (date: Date): string => {
  const d = new Date(date)
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
}

const formatForSelect = (locks: { id: string; name: string }[]): LockOption[] =>
  locks.map((lock) => ({ id: lock.id, label: lock.name, name: lock.name, text: lock.name }))

const RemoteLockSection = ({
  isAvailable,
  isFobAvailable = false,
  isIntegratedId = null,
  buildingId,
  userId,
  clientId,
  showExpiry = false,
}: RemoteLockSectionProps) => {
  const { displaySnackbar } = useSnackbar()
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsRemoteLockBuildingConnected(buildingId)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [availableLocks, setAvailableLocks] = useState<LockOption[]>([])
  const [doorValue, setDoorValue] = useState<LockOption[]>([])
  const [isCreateNewUserPopupVisible, setIsCreateNewUserPopupVisible] = useState<boolean>(false)
  const [isUserConnected, setIsUserConnected] = useState<boolean>(false)
  const [showDisconnectedUsersList, setShowDisconnectedUsersList] = useState<boolean>(false)
  const [disconnectedUsers, setDisconnectedUsers] = useState<LockOption[]>([])
  const [selectedUserToConnect, setSelectedUserToConnect] = useState<LockOption | null>(null)
  const [isFobModalOpen, setIsFobModalOpen] = useState<boolean>(false)
  const [isRevokeFobPopupVisible, setIsRevokeFobPopupVisible] = useState<boolean>(false)
  const [fobIssuedDate, setFobIssuedDate] = useState<string | null>(null)
  const [expiryDate, setExpiryDate] = useState<Date | null>(null)

  const fetchAvailableLocks = async (): Promise<void> => {
    try {
      const { data } = await API.getRemoteLockDevices(buildingId)
      setAvailableLocks(formatForSelect(data))
    } catch (error) {
      console.error(error)
    }
  }

  const fetchUserLocks = async (): Promise<void> => {
    try {
      const { data } = await API.getRemoteLockUserAccess(buildingId, userId)

      if (!data.isConnected) return

      setIsUserConnected(true)
      if (data?.doors?.length) setDoorValue(formatForSelect(data.doors))
      if (data?.expiresAt) setExpiryDate(new Date(data.expiresAt))

      if (!showExpiry) {
        const { data: userData } = await API.getRemoteLockUserDevices(clientId)
        if (userData?.fobIssuedAt) setFobIssuedDate(formatFobDate(new Date(userData.fobIssuedAt)))
      }
    } catch {
      // Note: reset rather than leave stale - a later failed re-fetch (e.g. after some other
      // action reloads this) must not keep showing a resident as connected from a prior success.
      setIsUserConnected(false)
    }
  }

  const handleCreateNewUser = async (): Promise<void> => {
    setLoading(true)
    try {
      if (showExpiry) {
        await API.provideRemoteLockStaffAccess(buildingId, userId, new Date(), expiryDate)
      } else {
        await API.createRemoteLockUser(clientId)
      }
      setIsUserConnected(true)
      displaySnackbar('New Remote Lock user has been successfully created')
    } catch {
      displaySnackbar('Error occurred creating a new Remote Lock user. Please try again', true)
    } finally {
      setLoading(false)
      setIsCreateNewUserPopupVisible(false)
    }
  }

  const handleReassignDoors = async (): Promise<void> => {
    setLoading(true)
    try {
      const lockIds = doorValue.map((door) => door.id)
      if (showExpiry) {
        await Promise.all([
          API.provideRemoteLockStaffAccess(buildingId, userId, new Date(), expiryDate),
          API.updateRemoteLockUserAccess(buildingId, userId, lockIds),
        ])
      } else {
        await API.reassignDoorsRemoteLockUser(clientId, lockIds, expiryDate)
      }
      displaySnackbar('Access to doors was reassigned.')
    } catch {
      displaySnackbar('Error occurred during assignee doors. Please try again', true)
    } finally {
      setLoading(false)
      setIsCreateNewUserPopupVisible(false)
    }
  }

  const handleCheckConnectExistAcc = async (): Promise<void> => {
    setLoading(true)
    try {
      if (showExpiry) {
        await API.connectRemoteLockStaff(buildingId, { userId })
      } else {
        await API.connectRemoteLockUser({ clientId, remoteLockId: isIntegratedId })
      }
      setIsUserConnected(true)
      displaySnackbar('Remote Lock user has been successfully connected.')
    } catch {
      try {
        const { data } = await API.getRemoteLockDisconnectedUsers(buildingId)
        if (!data.length) {
          displaySnackbar('Unfortunately, no accounts could be linked.', true)
        } else {
          setDisconnectedUsers(formatForSelect(data))
          setShowDisconnectedUsersList(true)
        }
      } catch {
        displaySnackbar('Error occurred while fetching disconnected users. Please try again.', true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFobSuccess = (): void => {
    setFobIssuedDate(formatFobDate(new Date()))
  }

  const handleRevokeFob = async (): Promise<void> => {
    setLoading(true)
    try {
      await API.revokeRemoteLockFob(buildingId, userId)
      setFobIssuedDate(null)
      displaySnackbar('Fob/Card has been successfully revoked.')
    } catch {
      displaySnackbar('Error occurred revoking the Fob/Card. Please try again.', true)
    } finally {
      setLoading(false)
    }
  }

  const handleConnectManually = async (): Promise<void> => {
    if (!selectedUserToConnect) return
    setLoading(true)
    try {
      if (showExpiry) {
        await API.connectRemoteLockStaff(buildingId, {
          remoteLockId: selectedUserToConnect.id,
          userId,
        })
      } else {
        await API.connectRemoteLockUser({ clientId, remoteLockId: selectedUserToConnect.id })
      }
      displaySnackbar('Remote Lock user has been successfully connected.')
      setIsUserConnected(true)
    } catch {
      displaySnackbar('Error occurred during auto connection. Please try again', true)
    } finally {
      setShowDisconnectedUsersList(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAvailable) {
      fetchAvailableLocks()
      fetchUserLocks()
    }
  }, [isAvailable])

  if (!isAvailable) return null

  if (isLoadingBuildingConnection) {
    return (
      <div className="information-element information-element-column">
        <Loader />
      </div>
    )
  }

  if (!isBuildingConnected) {
    return (
      <div className="information-element information-element-column">
        <Alert
          type="error"
          title="Remote Lock isn't connected"
          message="This building's Remote Lock integration isn't connected right now. Contact your admin to reconnect it before managing this resident's access."
        />
      </div>
    )
  }

  return (
    <>
      <div
        className={`information-element${isFobAvailable ? ' information-element--no-bottom-border' : ''}`}
      >
        <div className="element-name">Remote Lock (Schlage)</div>
        {showDisconnectedUsersList && disconnectedUsers.length ? (
          <div className="columns">
            <div className="column">
              <Select
                isClearable
                classNamePrefix="select"
                options={disconnectedUsers}
                styles={defaultDropdownStyles()}
                value={selectedUserToConnect}
                placeholder="Select User..."
                onChange={(option: SingleValue<LockOption>) => setSelectedUserToConnect(option)}
              />
              <button
                type="button"
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                disabled={isLoading}
                onClick={handleConnectManually}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : !isUserConnected ? (
          <div className="columns">
            <div className="column">
              <button
                type="button"
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                onClick={() => setIsCreateNewUserPopupVisible(true)}
              >
                Create New User
              </button>
              <div>or</div>
              <button
                type="button"
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                onClick={handleCheckConnectExistAcc}
              >
                Connect Existing Account
              </button>
            </div>
          </div>
        ) : showExpiry ? (
          <div className="columns">
            <div className="description-column">
              <div className="dropdown-buttons-container">
                <div className="dropdown-buttons-container-row">
                  <div className="dropdown-container">
                    <div className="dropdown-header">Door</div>
                    <Select
                      isClearable
                      isMulti
                      classNamePrefix="select"
                      options={availableLocks}
                      styles={defaultDropdownStyles()}
                      value={doorValue}
                      placeholder="Select Door..."
                      onChange={(options: readonly LockOption[]) => setDoorValue([...options])}
                    />
                  </div>
                  <div className="dropdown-container">
                    <div className="dropdown-header">Expiration Date</div>
                    <DatePicker
                      isClearable
                      className="dropdown-button"
                      placeholderText="MM/DD/YYYY"
                      selected={expiryDate}
                      minDate={new Date()}
                      onChange={(date: Date | null) => setExpiryDate(date)}
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                className={`confirm-button button-primary button ${isLoading ? 'is-loading' : ''}`}
                disabled={isLoading}
                onClick={handleReassignDoors}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : (
          <div className="columns">
            <div className="column">
              <Select
                isClearable
                isMulti
                classNamePrefix="select"
                options={availableLocks}
                styles={defaultDropdownStyles()}
                value={doorValue}
                placeholder="Select Door..."
                onChange={(options: readonly LockOption[]) => setDoorValue([...options])}
              />
              <button
                type="button"
                className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
                disabled={isLoading}
                onClick={handleReassignDoors}
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
      {isFobAvailable && (
        <div className="information-element information-element--no-top-border">
          <div className="element-name remote-lock-fob-title">
            Issue a Remote Lock physical Fob/Card
            <Tooltip triggerIcon={InfoIcon}>{FOB_TOOLTIP_TEXT}</Tooltip>
          </div>
          <div className="columns">
            <div className="column column-right">
              {fobIssuedDate ? (
                <>
                  <span className="remote-lock-fob-date">Fob/Card issued on {fobIssuedDate}</span>
                  <button
                    type="button"
                    className={`button button-danger auto-width ${isLoading ? 'is-loading' : ''}`}
                    disabled={isLoading}
                    onClick={() => setIsRevokeFobPopupVisible(true)}
                  >
                    Revoke Fob/Card
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="button button-primary auto-width"
                  disabled={!isUserConnected}
                  onClick={() => setIsFobModalOpen(true)}
                >
                  Issue Fob/Card
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmPopup
        title="Caution"
        body="Are you sure you want to create a new Remote Lock user?"
        isOpen={isCreateNewUserPopupVisible}
        cancelText="Close"
        confirmText="Yes, create"
        cancelCallback={() => setIsCreateNewUserPopupVisible(false)}
        confirmCallback={handleCreateNewUser}
        className="is-caution"
        isLoading={isLoading}
      />
      <ConfirmPopup
        title="Caution"
        body="Are you sure you want to revoke the Fob/Card?"
        isOpen={isRevokeFobPopupVisible}
        cancelText="No"
        confirmText="Yes"
        cancelCallback={() => setIsRevokeFobPopupVisible(false)}
        confirmCallback={() => {
          setIsRevokeFobPopupVisible(false)
          handleRevokeFob()
        }}
        className="is-caution"
        isLoading={isLoading}
      />
      <RemoteLockIssueFobModal
        isOpen={isFobModalOpen}
        buildingId={buildingId}
        userId={userId}
        onClose={() => setIsFobModalOpen(false)}
        onSuccess={handleFobSuccess}
      />
    </>
  )
}

export default RemoteLockSection
